<template>
    <form-container>
        <step-form-heading :icon="faArrowsDownToPeople">
            New Import

            <template v-slot:description> Create and update Voxie contacts in bulk. </template>
        </step-form-heading>

        <form-wrapper :sections="sections">
            <contact-import-new-context v-if="FEATURES.local_attributes" id="context"></contact-import-new-context>
            <contact-import-new-template id="template"></contact-import-new-template>
            <contact-import-new-subscription-status
                v-if="onCorporate()"
                id="status"
                v-model="subscriptions"
            ></contact-import-new-subscription-status>
            <contact-import-new-tags id="tags" v-model="tags"></contact-import-new-tags>
            <div id="upload">
                <contact-import-new-upload @selected="selectedFile"></contact-import-new-upload>
            </div>

            <vx-sticky-footer>
                <vx-button href="/contacts/import" class="w-20" size="lg" color="muted"> Cancel </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    :disabled="!file"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="submitting"
                >
                    Import
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </form-container>
</template>

<script setup>
import { VxButton, VxStickyFooter } from '@voxie/frontend-components';
import { inject, ref } from 'vue';
import StepFormHeading from '../../../general/StepFormHeading.vue';
import { faArrowsDownToPeople } from '@fortawesome/pro-solid-svg-icons';
import ContactImportNewContext from './ContactImportNewContext.vue';
import ContactImportNewTemplate from './ContactImportNewTemplate.vue';
import ContactImportNewSubscriptionStatus from './ContactImportNewSubscriptionStatus.vue';
import ContactImportNewTags from './ContactImportNewTags.vue';
import { contactsClient, groupImportsClient } from '~/services';
import ContactImportNewUpload from './ContactImportNewUpload.vue';
import { useToasted } from '~/composables/useToasted';
import FormContainer from '../../../general/FormContainer.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { stringifyQueryString } from '~/utils/string';

const FEATURES = inject('FEATURES');

const teamId = Spark.state.currentTeam.id;

const toasted = useToasted();
const { onCorporate, getCurrentGroupId } = useCurrentGroup();

const sections = ref(
    [
        FEATURES.local_attributes && { id: 'context', label: 'Context' },
        { id: 'template', label: 'Template' },
        onCorporate() && { id: 'status', label: 'Status' },
        { id: 'tags', label: 'Tags' },
        { id: 'upload', label: 'Upload' },
    ].filter((s) => !!s)
);

const file = ref();
const tags = ref([]);
const subscriptions = ref([]);

const submitting = ref(false);

const selectedFile = (event) => {
    file.value = event;
};

const submit = async () => {
    if (submitting.value || !file.value) {
        return;
    }
    submitting.value = true;

    try {
        const { data } = onCorporate()
            ? await contactsClient.generateImportUploadUrl(teamId, file.value.name, tags.value, subscriptions.value)
            : await groupImportsClient.generateContactsImportUploadUrl(
                  teamId,
                  getCurrentGroupId(),
                  file.value.name,
                  tags.value
              );

        if (!data?.upload_url) {
            throw new Error('Could not parse the upload url');
        }

        if (onCorporate()) {
            await contactsClient.importCsvFile(data.upload_url, file.value);
        } else {
            await groupImportsClient.importContactsCsvFile(data.upload_url, file.value);
        }

        window.location.assign(
            `/contacts/import?${stringifyQueryString({
                import_id: data.import_id,
                file_name: file.value.name,
                success: 1,
            })}`
        );
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    } finally {
        submitting.value = false;
    }
};
</script>
